import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';

/**
 * A controlled numeric input that keeps a local string buffer so the user can
 * type intermediate states like "-", "-0", "-0." without the form swallowing
 * the value. The form field is only updated when the string parses to a valid
 * finite number.
 *
 * Drop-in replacement for `<Input type="number">` in react-hook-form fields
 * where negative or decimal values are expected.
 */
export function NumericField({
  value,
  onChange,
  step,
  min,
  max,
  className,
  disabled,
  placeholder,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: string;
  min?: string;
  max?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [localValue, setLocalValue] = useState(() => String(value));
  const lastPropValue = useRef(value);

  // Sync from form → local when the form value changes externally (e.g.
  // programmatic setValue calls).
  useEffect(() => {
    if (value !== lastPropValue.current) {
      lastPropValue.current = value;
      setLocalValue(String(value));
    }
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setLocalValue(raw);
      const parsed = parseFloat(raw);
      if (Number.isFinite(parsed)) {
        lastPropValue.current = parsed;
        onChange(parsed);
      }
    },
    [onChange],
  );

  const handleBlur = useCallback(() => {
    // On blur, if the local string isn't a valid number, reset to the last
    // known good form value.
    const parsed = parseFloat(localValue);
    if (!Number.isFinite(parsed)) {
      setLocalValue(String(value));
    }
  }, [localValue, value]);

  return (
    <Input
      type='text'
      inputMode='decimal'
      step={step}
      min={min}
      max={max}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}
